import { type } from 'arktype';
import type { CustomPropertiesData } from '@zag/core/models';
import type { AbsencePortion } from './absence-payload.model';

export const absencePortionSchema = type({
  'portion?': "'full' | 'fraction' | 'hours' | 'range'",
  'fractionValue?': 'number',
  'durationMinutes?': 'number',
  'startTime?': 'string',
  'endTime?': 'string',
});

export type AbsencePortionFields = typeof absencePortionSchema.t;

export function portionCouplingError(payload: AbsencePortionFields): string | null {
  const hasFraction = payload.fractionValue !== undefined;
  const hasHours = payload.durationMinutes !== undefined;
  const hasStart = payload.startTime !== undefined;
  const hasEnd = payload.endTime !== undefined;
  const hasRange = hasStart || hasEnd;
  switch (payload.portion) {
    case undefined:
      return hasFraction || hasHours || hasRange ? 'a portion matching its value fields' : null;
    case 'full':
      return hasFraction || hasHours || hasRange ? 'a full portion without value fields' : null;
    case 'fraction':
      return hasFraction && !hasHours && !hasRange ? null : 'a fraction portion with fractionValue only';
    case 'hours':
      return hasHours && !hasFraction && !hasRange ? null : 'an hours portion with durationMinutes only';
    case 'range':
      return hasStart && hasEnd && !hasFraction && !hasHours ? null : 'a range portion with startTime and endTime only';
    default:
      return 'a valid portion';
  }
}

export const createTimetrackingAbsenceBaseSchema = type({
  'source?': 'string',
  'externalId?': 'string',
  personId: 'string',
  dateFrom: 'string',
  'dateTo?': 'string | null',
  'absenceType?': 'string',
  'title?': 'string',
  'description?': 'string | null',
  'color?': 'string | null',
  'meta?': 'unknown',
})
  .and(absencePortionSchema)
  .narrow((payload, ctx) => portionCouplingError(payload) === null || ctx.mustBe('a portion with exactly its matching value fields'));

export const createTimetrackingAbsenceSchema = createTimetrackingAbsenceBaseSchema.onUndeclaredKey('reject');

export type CreateTimetrackingAbsence = typeof createTimetrackingAbsenceSchema.t;

export const pushTimetrackingAbsencesSchema = type({
  absences: createTimetrackingAbsenceSchema.array(),
});

export type PushTimetrackingAbsences = typeof pushTimetrackingAbsencesSchema.t;

export const updateTimetrackingAbsenceBaseSchema = type({
  'source?': 'string',
  'externalId?': 'string | null',
  'personId?': 'string',
  'dateFrom?': 'string',
  'dateTo?': 'string | null',
  'absenceType?': 'string',
  'title?': 'string',
  'description?': 'string | null',
  'color?': 'string | null',
  'meta?': 'unknown',
})
  .and(absencePortionSchema)
  .narrow((payload, ctx) => portionCouplingError(payload) === null || ctx.mustBe('a portion with exactly its matching value fields'));

export const updateTimetrackingAbsenceSchema = updateTimetrackingAbsenceBaseSchema.onUndeclaredKey('reject');

export type UpdateTimetrackingAbsence = typeof updateTimetrackingAbsenceSchema.t;

export interface TimetrackingAbsence {
  absenceId: string;
  personId: string;
  calendarId: string;
  calendarSlotId: string;
  dateFrom: string;
  dateTo: string | null;
  source: string;
  externalId: string | null;
  absenceType: string;
  title: string;
  description: string | null;
  color: string;
  meta: unknown;
  portion?: AbsencePortion;
  startTime?: string;
  endTime?: string;
  fractionValue?: number;
  durationMinutes?: number;
  isAllDay?: boolean;
  /** Server-stamped copy of the absence type's custom-property values at booking time. Read-only. */
  data?: CustomPropertiesData;
  createdAt: string;
  updatedAt: string;
}

export interface TimetrackingAbsencePushResult {
  source: string;
  externalId: string | null;
  calendarId: string;
  calendarSlotId: string;
  absenceId: string;
  status: 'created' | 'updated';
}

export interface TimetrackingAbsencePushResponse {
  processed: number;
  results: TimetrackingAbsencePushResult[];
}
